import React from 'react';
import { fs, makeFields, FarAiAssist, Getter, Setter } from './farFields';

/* FAR Part 12 — Commercial Products and Services (8 sections).
 * Renders when the FAR 12 badge is active. get/set are bound to the far12 slice. */

export default function Far12Form({ get, set }: { get: Getter; set: Setter }) {
  const F = makeFields(get, set);

  return (
    <div>
      <div style={{ padding: '10px 14px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>FAR Part 12 — Commercial Products and Services</span>
        <p style={{ fontSize: 11, color: '#047857', margin: '2px 0 0' }}>Layers onto the selected Part 13/14/15 procedure. Complete the commercial-item requirements below.</p>
      </div>
      <FarAiAssist formName="FAR Part 12 — Commercial Determination & Market Research" get={get} set={set}
        fields={[
          { key: 'cp_determination', label: 'Commerciality Determination', kind: 'long' },
          { key: 'mr_customary_practices', label: 'Customary Commercial Practices', kind: 'long' },
          { key: 'ct_price_narrative', label: 'Price Analysis Narrative', kind: 'long' },
        ]} />

      {/* Section 1 — Commerciality Determination */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 1 · Commerciality Determination</h3>
        {F.Sel('cp_type', 'Commercial Product or Service Type', ['Commercial product', 'Commercial service', 'Commercially available off-the-shelf (COTS) item', 'Nondevelopmental item', 'Combination'], { req: true })}
        {F.Long('cp_determination', 'Commerciality Determination', { req: true })}
        {F.MultiSelect('cp_basis', 'Commerciality Basis', ['Sold to the general public', 'Offered for sale to the general public', 'Evolved from a commercial product', 'Minor modification of a commercial product', 'Commercial service', 'Offered competitively in substantial quantities', 'COTS item', 'Prior Government commerciality determination', 'Other permitted basis'])}
        {F.TableField('cp_comparables', 'Comparable Commercial Offerings', [
          { key: 'product', label: 'Product or service', type: 'text' },
          { key: 'provider', label: 'Provider', type: 'text' },
          { key: 'customer', label: 'Commercial customer type', type: 'text' },
          { key: 'price', label: 'Market price', type: 'text' },
          { key: 'terms', label: 'Commercial terms', type: 'long' },
          { key: 'source', label: 'Source', type: 'text' },
          { key: 'notes', label: 'Comparison notes', type: 'long' },
        ])}
        {F.YN('cp_prior_determination', 'Prior Commerciality Determination Available?')}
        {get('cp_prior_determination') === 'Yes' && F.Text('cp_prior_reference', 'Prior Determination Reference')}
        {F.Sel('cp_status', 'Commerciality Determination Status', ['Draft', 'In Review', 'Approved', 'Rejected'])}
        {F.Text('cp_approved_by', 'Determination Approved By')}
        {F.Date_('cp_approved_date', 'Approval Date')}
      </div>

      {/* Section 2 — Commercial Market Research */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 2 · Commercial Market Research</h3>
        {F.YN('mr_completed', 'Was commercial market research completed?', ['Yes', 'No', 'In Progress'])}
        {F.YN('mr_solutions_available', 'Are commercial solutions available?', ['Yes', 'No', 'Partially'])}
        {F.YN('mr_can_modify', 'Can the requirement be modified to use a commercial solution?', ['Yes', 'No', 'Not Applicable'])}
        {F.Long('mr_customary_practices', 'Customary Commercial Practices')}
        {F.ListField('mr_standard_terms', 'Standard Market Terms')}
        {F.Long('mr_warranty', 'Typical Warranty')}
        {F.Long('mr_licensing', 'Typical Licensing or Subscription Terms')}
        {F.Long('mr_inspection', 'Typical Inspection and Acceptance Practices')}
        {F.TableField('mr_pricing_sources', 'Commercial Pricing Sources', [
          { key: 'source', label: 'Source', type: 'text' },
          { key: 'date', label: 'Date', type: 'text' },
          { key: 'product', label: 'Product/service', type: 'text' },
          { key: 'unit', label: 'Unit', type: 'text' },
          { key: 'price', label: 'Price', type: 'text' },
          { key: 'discount', label: 'Discount', type: 'text' },
          { key: 'evidence', label: 'Evidence', type: 'text' },
        ])}
      </div>

      {/* Section 3 — Solicitation and Award Method */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 3 · Solicitation and Award Method</h3>
        {F.Sel('sa_procedure', 'Selected Procedure', ['FAR Part 13 simplified acquisition', 'FAR Part 14 sealed bidding', 'FAR Part 15 negotiated acquisition', 'FAR 12.6 streamlined procedure'])}
        {F.Long('sa_rationale', 'Procedure Selection Rationale')}
        {F.YN('sa_sf1449', 'SF 1449 Required?', ['Yes', 'No', 'System Determination'])}
        {F.YN('sa_multiple_offers', 'Multiple Offers Permitted?')}
        {F.YN('sa_literature_sufficient', 'Existing Commercial Literature Sufficient?')}
        {F.YN('sa_unique_proposal', 'Unique Technical Proposal Required?')}
        {get('sa_unique_proposal') === 'Yes' && F.Long('sa_unique_reason', 'Reason Unique Proposal Is Required')}
      </div>

      {/* Section 4 — Evaluation Approach */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 4 · Evaluation Approach</h3>
        {F.Sel('ev_method', 'Evaluation Method', ['Price only', 'Lowest price technically acceptable', 'Best-value tradeoff', 'Comparative evaluation', 'Other'])}
        {F.YN('ev_past_performance', 'Past Performance Evaluated?')}
        {get('ev_past_performance') === 'Yes' && F.MultiSelect('ev_pp_sources', 'Past Performance Sources', ['CPARS', 'Government references', 'Commercial references', 'Public information', 'Offeror-provided information', 'Other'])}
        {F.TableField('ev_factors', 'Evaluation Factors', [
          { key: 'factor', label: 'Factor', type: 'text' },
          { key: 'type', label: 'Type', type: 'select', options: ['Pass/fail', 'Rated', 'Evaluated'] },
          { key: 'weight', label: 'Weight/importance', type: 'text' },
          { key: 'standard', label: 'Standard', type: 'text' },
          { key: 'evidence', label: 'Evidence required', type: 'text' },
        ])}
        {F.YN('ev_stated_in_solicitation', 'Evaluation Factors Clearly Stated in Solicitation?')}
      </div>

      {/* Section 5 — Contract Type and Pricing */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 5 · Contract Type and Pricing</h3>
        {F.Sel('ct_type', 'Contract Type', ['Firm-fixed-price', 'Fixed-price with economic price adjustment', 'Time-and-materials', 'Labor-hour', 'Other authorized type'])}
        {F.YN('ct_tm_lh', 'Is Time-and-Materials or Labor-Hour Proposed?')}
        {get('ct_tm_lh') === 'Yes' && <>
          {F.Long('ct_tm_unsuitable', 'Why another contract type is unsuitable')}
          {F.Long('ct_tm_dnf', 'Determination and Findings')}
          {F.Money('ct_tm_ceiling', 'Ceiling Price')}
          {F.Text('ct_tm_co_approval', 'Contracting-Officer Approval')}
          {F.YN('ct_tm_altI', 'Alternate I to FAR 52.212-4 Reviewed?')}
        </>}
        {F.MultiSelect('ct_price_method', 'Price Reasonableness Method', ['Competitive quotations or offers', 'Published market prices', 'Historical prices', 'Comparable commercial transactions', 'Parametric comparison', 'Data other than certified cost or pricing data', 'Other'])}
        {F.Long('ct_price_narrative', 'Price Analysis Narrative')}
        {F.Sel('ct_price_determination', 'Price Reasonableness Determination', ['Reasonable', 'Unreasonable', 'More Data Required'])}
      </div>

      {/* Section 6 — Commercial Terms and Clauses */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 6 · Commercial Terms and Clauses</h3>
        {F.YN('cl_2121', 'FAR 52.212-1 Included?')}
        {F.YN('cl_2122', 'FAR 52.212-2 or Equivalent Evaluation Language Included?')}
        {F.YN('cl_2123', 'FAR 52.212-3 Included?')}
        {F.YN('cl_2124', 'FAR 52.212-4 Included?')}
        {F.YN('cl_2125', 'FAR 52.212-5 Included and Completed?')}
        {F.YN('cl_tailored', 'Are 52.212-1 or 52.212-4 Tailored?')}
        {get('cl_tailored') === 'Yes' && <>
          {F.TableField('cl_tailoring', 'Tailoring Detail', [
            { key: 'provision', label: 'Provision/paragraph', type: 'text' },
            { key: 'original', label: 'Original text', type: 'long' },
            { key: 'revised', label: 'Revised text', type: 'long' },
            { key: 'basis', label: 'Market-practice basis', type: 'long' },
            { key: 'risk', label: 'Risk assessment', type: 'long' },
            { key: 'approval', label: 'Approval status', type: 'select', options: ['Draft', 'Pending', 'Approved', 'Rejected'] },
          ])}
          {F.YN('cl_conflict', 'Does Tailoring Conflict With Customary Commercial Practice?')}
          {F.YN('cl_waiver', 'Waiver or Approval Required?')}
        </>}
        {F.YN('cl_additional', 'Additional Agency Clauses Included?')}
        {get('cl_additional') === 'Yes' && F.Long('cl_additional_authority', 'Authority for Additional Clauses')}
      </div>

      {/* Section 7 — Inspection, Acceptance, and Warranty */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 7 · Inspection, Acceptance, and Warranty</h3>
        {F.YN('ia_standard_acceptance', 'Standard Commercial Acceptance Procedures Used?')}
        {F.Long('ia_acceptance_criteria', 'Acceptance Criteria')}
        {F.Long('ia_inspection_method', 'Inspection Method')}
        {F.YN('ia_complex', 'Is the Product or Service Complex or Mission-Critical?')}
        {F.YN('ia_alt_inspection', 'Alternative Inspection Procedures Required?')}
        {F.YN('ia_warranty', 'Commercial Warranty Available?')}
        {get('ia_warranty') === 'Yes' && F.Long('ia_warranty_terms', 'Warranty Terms')}
        {F.YN('ia_gov_warranty', 'Government-Specific Warranty Required?')}
        {get('ia_gov_warranty') === 'Yes' && F.Long('ia_gov_warranty_rationale', 'Government Warranty Rationale')}
      </div>

      {/* Section 8 — Final Review */}
      <div style={fs.section}>
        <h3 style={fs.title}>Section 8 · FAR Part 12 Final Review</h3>
        {F.Checklist('final_review', [
          'Commerciality determination is documented and approved.',
          'Market research supports use of FAR Part 12.',
          'Requirements reflect commercial market practices.',
          'Selected Part 13, 14, or 15 procedure is documented.',
          'Contract type is authorized.',
          'Price reasonableness is documented.',
          'Evaluation factors are stated.',
          'Past performance was considered.',
          'FAR 52.212 provisions and clauses were reviewed.',
          'Tailoring is documented and approved.',
          'Acceptance and warranty terms are defined.',
          'Applicable commercial-item laws were reviewed.',
          'Contracting officer approval is complete.',
        ])}
      </div>
    </div>
  );
}
